import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { usePager, Pager } from "@/components/kiyana/Pager";
import { Plus, X, FileText, Eye, Check, AlertTriangle, ClipboardList, Paperclip, Users, CalendarDays, Flag, Trash2 } from "lucide-react";
import EmpPicker from "@/components/EmpPicker";
import { MAROON, inpL } from "@/components/kiyana/LeaveForm";
import SpkFeedback, { SPK_STATUS_LABEL, SPK_STATUS_CHIP, PRIORITY_LABEL, PRIORITY_CHIP, fmtWaktu, fmtDeadline } from "@/components/kiyana/SpkFeedback";

const card = "bg-white rounded-2xl border border-slate-100";
const TABS = ["semua", "menunggu", "selesai_sebagian", "menunggu_verifikasi", "selesai", "perlu_perbaikan"];

function nowMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function CreateModal({ options, onClose, onSaved }) {
  const [form, setForm] = useState({ title: "", description: "", deadline: "", priority: "penting", checklist: [""], attachment_url: null, user_ids: [], role_ids: [] });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleRole = (rid) => set("role_ids", form.role_ids.includes(rid) ? form.role_ids.filter((r) => r !== rid) : [...form.role_ids, rid]);

  const submit = async () => {
    if (form.title.trim().length < 3) return toast.error("Judul SPK wajib diisi");
    if (!form.deadline) return toast.error("Tentukan deadline");
    if (!form.user_ids.length && !form.role_ids.length) return toast.error("Pilih minimal satu penerima");
    setBusy(true);
    try {
      let attachment_url = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        attachment_url = (await api.post("/uploads", fd)).data.url;
      }
      const r = await api.post("/spk", { title: form.title.trim(), description: form.description.trim(), deadline: form.deadline, priority: form.priority, checklist: form.checklist, attachment_url, assignee_user_ids: form.user_ids, assignee_role_ids: form.role_ids });
      toast.success(`SPK ${r.data.spk_no} dibuat — ${r.data.progress.length} penerima diberi notifikasi`);
      onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membuat SPK"); } finally { setBusy(false); }
  };

  return (
    <div data-testid="spkf-modal" className="fixed inset-0 z-50 bg-slate-900/40 flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-xl rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 sticky top-0 bg-white rounded-t-2xl z-10">
          <h3 className="font-display text-base font-bold text-slate-900">Buat SPK Baru</h3>
          <button data-testid="spkf-close" onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-50 flex items-center justify-center text-slate-400"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-[11px] font-semibold text-slate-500">Judul Tugas <span className="text-red-500">*</span></label>
            <input data-testid="spkf-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="mis. Stock Opname Gudang" className={inpL} />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500">Instruksi / Deskripsi</label>
            <textarea data-testid="spkf-desc" value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} placeholder="Tulis instruksi pengerjaan..." className="w-full px-3 py-2.5 rounded-xl bg-white border border-slate-200 text-xs text-slate-700 focus:outline-none focus:border-[#7F1D2D]" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500">Checklist Pengerjaan</label>
            <div className="space-y-1.5">
              {form.checklist.map((c, i) => (
                <div key={i} className="flex gap-1.5">
                  <input data-testid={`spkf-check-${i}`} value={c} onChange={(e) => set("checklist", form.checklist.map((x, j) => j === i ? e.target.value : x))} placeholder={`Langkah ${i + 1}...`} className={inpL} />
                  <button onClick={() => set("checklist", form.checklist.filter((_, j) => j !== i))} className="w-10 h-10 rounded-xl border border-slate-200 text-slate-400 hover:text-red-500 flex items-center justify-center"><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              <button data-testid="spkf-check-add" onClick={() => set("checklist", [...form.checklist, ""])} className="text-[11px] font-semibold text-[#7F1D2D] hover:underline">+ Tambah langkah</button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-semibold text-slate-500">Deadline <span className="text-red-500">*</span></label>
              <input data-testid="spkf-deadline" type="datetime-local" value={form.deadline} onChange={(e) => set("deadline", e.target.value)} className={inpL} />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-500">Prioritas</label>
              <select data-testid="spkf-priority" value={form.priority} onChange={(e) => set("priority", e.target.value)} className={inpL}>
                {Object.entries(PRIORITY_LABEL).map(([id, l]) => <option key={id} value={id}>{l}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500">Penerima — per divisi</label>
            <div className="flex gap-1.5 flex-wrap">
              {(options?.roles || []).map((r) => (
                <button key={r.id} data-testid={`spkf-role-${r.id}`} onClick={() => toggleRole(r.id)}
                  className={`h-8 px-3 rounded-full text-[11px] font-semibold border transition-colors ${form.role_ids.includes(r.id) ? "border-[#7F1D2D] bg-[#7F1D2D] text-white" : "border-slate-200 text-slate-500"}`}>{r.label}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500">Penerima — per karyawan</label>
            <EmpPicker tid="spkf-emp" options={options?.employees || []} value={form.user_ids} onChange={(v) => set("user_ids", v)} placeholder="Ketik nama karyawan" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500">Lampiran Instruksi (opsional)</label>
            <label data-testid="spkf-attachment" className="flex items-center gap-2 h-10 px-3 rounded-xl border border-dashed border-slate-300 text-xs text-slate-500 cursor-pointer hover:border-[#7F1D2D] hover:text-[#7F1D2D] transition-colors">
              <Paperclip className="w-3.5 h-3.5" /> <span className="truncate">{file ? file.name : "Pilih foto / PDF panduan..."}</span>
              <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
          </div>
          <button data-testid="spkf-submit" onClick={submit} disabled={busy} className="w-full h-11 rounded-xl text-white text-sm font-semibold disabled:opacity-40" style={{ background: MAROON }}>{busy ? "Membuat..." : "Buat & Kirim SPK"}</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminSpk() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState(nowMonth());
  const [priority, setPriority] = useState("");
  const [role, setRole] = useState("");
  const [empIds, setEmpIds] = useState([]);
  const [tab, setTab] = useState("semua");
  const [selId, setSelId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [vNote, setVNote] = useState("");
  const [confirmDel, setConfirmDel] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/spk", { params: { period, priority: priority || undefined, role: role || undefined, employee_id: empIds.join(",") || undefined } })
      .then((r) => setData(r.data)).catch((e) => { setData(null); toast.error(e?.response?.data?.detail || "Gagal memuat SPK"); });
  }, [period, priority, role, empIds]);
  useEffect(() => { load(); }, [load]);

  const loadDetail = useCallback((id) => {
    if (!id) { setDetail(null); return; }
    api.get(`/spk/${id}`).then((r) => setDetail(r.data)).catch(() => setDetail(null));
  }, []);
  useEffect(() => { loadDetail(selId); setVNote(""); setConfirmDel(false); }, [selId, loadDetail]);

  const items = useMemo(() => (data?.items || []).filter((i) => tab === "semua" || i.status === tab), [data, tab]);
  const { paged: pagedSpk, pager: spkPager } = usePager(items);
  const counts = data?.counts || {};
  const totalAll = Object.values(counts).reduce((s, n) => s + n, 0);

  const verify = async (action) => {
    if (!detail) return;
    if (action === "perbaikan" && vNote.trim().length < 3) return toast.error("Catatan perbaikan wajib diisi");
    setBusy(true);
    try {
      await api.put(`/spk/${detail.id}/verify`, { action, note: vNote.trim() });
      toast.success(action === "selesai" ? "SPK diverifikasi selesai" : "SPK dikembalikan untuk perbaikan");
      load(); loadDetail(detail.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memproses"); } finally { setBusy(false); }
  };

  const del = async () => {
    setBusy(true);
    try {
      await api.delete(`/spk/${detail.id}`);
      toast.success("SPK dihapus");
      setSelId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); } finally { setBusy(false); setConfirmDel(false); }
  };

  const doneCount = (s) => (s.progress || []).filter((p) => p.status === "selesai").length;
  return (
    <div data-testid="spk-page" className="space-y-5 pb-10">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Tugas / SPK</h1>
          <p className="text-xs text-slate-400 mt-0.5">Surat Perintah Kerja: instruksi, checklist, progres penerima, dan verifikasi HR.</p>
        </div>
        <button data-testid="spk-create-btn" onClick={() => setShowCreate(true)} className="h-10 px-4 rounded-xl text-white text-sm font-semibold flex items-center gap-1.5 transition-colors" style={{ background: MAROON }}><Plus className="w-4 h-4" /> Buat SPK</button>
      </div>

      <div className={`${card} p-4 grid grid-cols-2 lg:grid-cols-4 gap-3`}>
        <div>
          <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Periode Deadline</label>
          <input data-testid="spk-filter-period" type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className={inpL} />
        </div>
        <div>
          <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Prioritas</label>
          <select data-testid="spk-filter-priority" value={priority} onChange={(e) => setPriority(e.target.value)} className={inpL}>
            <option value="">Semua Prioritas</option>
            {Object.entries(PRIORITY_LABEL).map(([id, l]) => <option key={id} value={id}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Divisi</label>
          <select data-testid="spk-filter-role" value={role} onChange={(e) => setRole(e.target.value)} className={inpL}>
            <option value="">Semua Divisi</option>
            {(data?.options?.roles || []).map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Karyawan</label>
          <EmpPicker tid="spk-filter-emp" options={data?.options?.employees || []} value={empIds} onChange={setEmpIds} placeholder="Ketik nama karyawan" />
        </div>
      </div>

      <div className="flex items-center gap-1 border-b border-slate-100 overflow-x-auto">
        {TABS.map((t) => {
          const n = t === "semua" ? totalAll : (counts[t] || 0);
          return (
            <button key={t} data-testid={`spk-tab-${t}`} onClick={() => setTab(t)}
              className={`px-3 py-2.5 text-[11px] font-semibold whitespace-nowrap border-b-2 transition-colors flex items-center gap-1.5 ${tab === t ? "border-[#7F1D2D] text-[#7F1D2D]" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
              {t === "semua" ? "Semua" : SPK_STATUS_LABEL[t]} <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${tab === t ? "bg-[#7F1D2D] text-white" : "bg-slate-100 text-slate-400"}`}>{n}</span>
            </button>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-[1fr_420px] gap-5 items-start">
        <div className={`${card} overflow-hidden`}>
          {!data && <div className="py-10 text-center text-xs text-slate-400">Memuat...</div>}
          {data && !items.length && <div data-testid="spk-empty" className="py-10 text-center text-xs text-slate-400">Belum ada SPK pada filter ini.</div>}
          {pagedSpk.map((s) => {
            const done = doneCount(s), total = (s.progress || []).length;
            return (
              <div key={s.id} data-testid={`spk-row-${s.id}`} className={`px-5 py-4 border-b border-slate-50 last:border-0 transition-colors ${selId === s.id ? "bg-[#FBF3F4]" : "hover:bg-slate-50/50"}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold text-slate-900">{s.title}</span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${PRIORITY_CHIP[s.priority]}`}>{PRIORITY_LABEL[s.priority]}</span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${SPK_STATUS_CHIP[s.status]}`}>{SPK_STATUS_LABEL[s.status]}</span>
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5">{s.spk_no} • {total} penerima • deadline {fmtDeadline(s.deadline)}</div>
                    <div className="flex items-center gap-2 mt-2">
                      <div className="flex-1 max-w-[180px] h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${total ? (done / total) * 100 : 0}%`, background: MAROON }} />
                      </div>
                      <span className="text-[9px] text-slate-400">{done} dari {total} selesai</span>
                    </div>
                  </div>
                  <button data-testid={`spk-detail-btn-${s.id}`} onClick={() => setSelId(s.id)} className="h-8 px-3 rounded-lg border text-[11px] font-semibold flex-shrink-0 transition-colors" style={{ borderColor: MAROON, color: MAROON }}>Detail</button>
                </div>
              </div>
            );
          })}
          <Pager {...spkPager} testid="task-pager" />
        </div>

        <div className={`${card} p-5 space-y-4 lg:sticky lg:top-4`} data-testid="spk-detail-panel">
          {!detail && <div className="py-16 text-center text-xs text-slate-400">Pilih SPK untuk melihat detail.</div>}
          {detail && (
            <>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-display text-base font-bold text-slate-900 truncate">{detail.title}</h3>
                  <p className="text-[10px] text-slate-400">{detail.spk_no}</p>
                </div>
                <span data-testid="spk-detail-status" className={`text-[10px] px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ${SPK_STATUS_CHIP[detail.status]}`}>{SPK_STATUS_LABEL[detail.status]}</span>
              </div>
              <div className="grid grid-cols-2 gap-y-2.5 text-[11px] border-t border-slate-50 pt-3">
                <div className="text-slate-400 flex items-center gap-1.5"><Users className="w-3 h-3" /> Dibuat oleh</div><div className="text-right font-semibold text-slate-700">{detail.created_by}</div>
                <div className="text-slate-400 flex items-center gap-1.5"><CalendarDays className="w-3 h-3" /> Deadline</div><div className="text-right font-semibold text-slate-700">{fmtDeadline(detail.deadline)}</div>
                <div className="text-slate-400 flex items-center gap-1.5"><Flag className="w-3 h-3" /> Prioritas</div><div className="text-right font-semibold text-[#7F1D2D]">{PRIORITY_LABEL[detail.priority]}</div>
                <div className="text-slate-400 flex items-center gap-1.5"><ClipboardList className="w-3 h-3" /> Penerima</div><div className="text-right font-semibold text-slate-700">{(detail.progress || []).length} karyawan</div>
              </div>
              {detail.description && (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Instruksi Tugas</div>
                  <div className="bg-slate-50 rounded-lg p-3 text-[11px] text-slate-600">{detail.description}</div>
                </div>
              )}
              {(detail.checklist || []).length > 0 && (
                <div className="space-y-1">
                  {(detail.checklist || []).map((c) => (
                    <div key={c.id} className="flex items-center gap-2 text-[11px] text-slate-600">
                      <span className="w-3.5 h-3.5 rounded border border-slate-300 flex-shrink-0" /> {c.text}
                    </div>
                  ))}
                </div>
              )}
              {detail.attachment_url && (
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-slate-400 font-semibold">Lampiran Instruksi</span>
                  <a data-testid="spk-attachment-link" href={detail.attachment_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-sky-600 font-semibold hover:underline"><FileText className="w-3.5 h-3.5" /> dokumen <Eye className="w-3 h-3" /> Lihat</a>
                </div>
              )}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Progres Penerima</div>
                  <span className="text-[10px] text-slate-400">{doneCount(detail)} dari {(detail.progress || []).length} karyawan</span>
                </div>
                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mb-2">
                  <div className="h-full rounded-full" style={{ width: `${(detail.progress || []).length ? (doneCount(detail) / detail.progress.length) * 100 : 0}%`, background: MAROON }} />
                </div>
                <div className="space-y-1.5 max-h-44 overflow-y-auto pr-1">
                  {(detail.progress || []).map((p) => (
                    <div key={p.user_id} data-testid={`spk-progress-${p.user_id}`} className="flex items-center gap-2 bg-slate-50/70 rounded-lg px-2.5 py-2">
                      <span className="w-7 h-7 rounded-full bg-[#FBF3F4] text-[#7F1D2D] flex items-center justify-center text-[10px] font-bold flex-shrink-0">{(p.user_name || "?")[0]}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] font-semibold text-slate-700 truncate">{p.user_name}</div>
                        <div className="text-[9px] text-slate-400">{p.status === "selesai" ? fmtWaktu(p.done_at) : "—"}{p.note ? ` • ${p.note}` : ""}</div>
                        {p.proof_url && <a href={p.proof_url} target="_blank" rel="noreferrer" className="text-[9px] text-sky-600 font-semibold hover:underline">Lihat bukti pengerjaan</a>}
                      </div>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold flex-shrink-0 ${p.status === "selesai" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>{p.status === "selesai" ? "Sudah Dikerjakan" : "Belum Konfirmasi"}</span>
                    </div>
                  ))}
                </div>
              </div>

              {detail.status === "menunggu_verifikasi" && (
                <div className="space-y-2.5 border-t border-slate-50 pt-3">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Verifikasi HR</div>
                  <textarea data-testid="spk-verify-note" value={vNote} onChange={(e) => setVNote(e.target.value)} rows={2} placeholder="Catatan verifikasi (wajib saat minta perbaikan)..." className="w-full px-3 py-2.5 rounded-xl bg-white border border-slate-200 text-xs text-slate-700 focus:outline-none focus:border-[#7F1D2D]" />
                  <div className="grid grid-cols-2 gap-2">
                    <button data-testid="spk-verify-revise" onClick={() => verify("perbaikan")} disabled={busy} className="h-10 rounded-xl border border-red-200 text-red-500 text-xs font-semibold hover:bg-red-50 transition-colors disabled:opacity-40">Minta Perbaikan</button>
                    <button data-testid="spk-verify-finish" onClick={() => verify("selesai")} disabled={busy} className="h-10 rounded-xl text-white text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-40" style={{ background: MAROON }}><Check className="w-3.5 h-3.5" /> Verifikasi Selesai</button>
                  </div>
                </div>
              )}
              {detail.status === "perlu_perbaikan" && (
                <div className="flex items-start gap-2 text-[11px] text-red-600 bg-red-50 border border-red-100 rounded-xl p-3">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> Perlu perbaikan: {detail.verify_note}
                </div>
              )}
              {detail.status === "selesai" && (
                <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl p-3">Diverifikasi selesai oleh {detail.verified_by} • {fmtWaktu(detail.verified_at)}{detail.verify_note ? ` — ${detail.verify_note}` : ""}</div>
              )}

              <div className="border-t border-slate-50 pt-3">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Feedback & Diskusi</div>
                <SpkFeedback spkId={detail.id} feedback={detail.feedback || []} onChanged={() => loadDetail(detail.id)} tid="spk-fb" />
              </div>

              {detail.status !== "selesai" && (
                !confirmDel
                  ? <button data-testid="spk-delete-btn" onClick={() => setConfirmDel(true)} className="w-full h-9 rounded-xl border border-slate-200 text-slate-400 text-[11px] font-semibold hover:text-red-500 hover:border-red-200 transition-colors flex items-center justify-center gap-1.5"><Trash2 className="w-3.5 h-3.5" /> Hapus SPK</button>
                  : <div className="grid grid-cols-2 gap-2">
                      <button data-testid="spk-delete-no" onClick={() => setConfirmDel(false)} className="h-9 rounded-xl border border-slate-200 text-slate-500 text-[11px] font-semibold">Tidak Jadi</button>
                      <button data-testid="spk-delete-yes" onClick={del} disabled={busy} className="h-9 rounded-xl bg-red-500 text-white text-[11px] font-semibold disabled:opacity-40">{busy ? "Menghapus..." : "Ya, Hapus"}</button>
                    </div>
              )}
            </>
          )}
        </div>
      </div>

      {showCreate && <CreateModal options={data?.options} onClose={() => setShowCreate(false)} onSaved={load} />}
    </div>
  );
}
